import React from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Switch,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import PeopleIcon from "@mui/icons-material/People";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import TablePaginationActions from "../../../../../globals/components/TablePaginationActions";

function LawFirmUsersTabPanel({
  id,
  navigate,
  role,
  userSearchQuery,
  handleUserSearch,
  clearUserSearch,
  filteredTeamMembers,
  teamMembers,
  paginatedTeamMembers,
  teamMembersPage,
  teamMembersRowsPerPage,
  handleTeamMembersPageChange,
  handleTeamMembersRowsPerPageChange,
  handleToggle,
  setTeamMembers,
  handleOpenReassignLogs,
  openReassignDialog,
  handleDeleteUserClick,
}) {
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 3,
          backgroundColor: "#e3f2fd", // Light blue background
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "600",
            color: "#1976d2", // Blue text
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <PeopleIcon />
          Manage Law Firm Users
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            onClick={handleOpenReassignLogs}
            variant="outlined"
            color="primary"
            startIcon={<SwapHorizIcon />}
            sx={{ borderRadius: "20px", whiteSpace: "nowrap" }}
          >
            View Re-Assignments
          </Button>
          <Button
            onClick={() => navigate(`/addlawfirmuser/${id}`)}
            variant="contained"
            color="primary" // Changed to primary (blue) for consistency
            startIcon={<AddIcon />}
            sx={{ borderRadius: "20px", whiteSpace: "nowrap" }}
          >
            Add New User
          </Button>
        </Box>
      </Box>

      {/* Search Field for Users */}
      <Box
        sx={{
          p: 3,
          backgroundColor: "#f8f9fa",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search users by name..."
          value={userSearchQuery}
          onChange={handleUserSearch}
          slotProps={{ input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#1976d2" }} />
              </InputAdornment>
            ),
            endAdornment: userSearchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={clearUserSearch}
                  sx={{ color: "#1976d2" }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#1976d2",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#1976d2",
              },
            },
          }}
        />
        {userSearchQuery && (
          <Typography variant="body2" sx={{ mt: 1, color: "#1976d2" }}>
            Showing {filteredTeamMembers.length} of {teamMembers.length} users
          </Typography>
        )}
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                User Name
              </TableCell>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                Email
              </TableCell>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                Location
              </TableCell>
              <TableCell sx={{ fontWeight: "600", fontSize: "0.9rem" }}>
                Position
              </TableCell>
              <TableCell
                sx={{ fontWeight: "600", fontSize: "0.9rem" }}
                align="center"
              >
                Admin
              </TableCell>
              <TableCell
                sx={{ fontWeight: "600", fontSize: "0.9rem" }}
                align="center"
              >
                Active
              </TableCell>
              <TableCell
                sx={{ fontWeight: "600", fontSize: "0.9rem" }}
                align="center"
              >
                Default Attorney
              </TableCell>
              <TableCell
                sx={{ fontWeight: "600", fontSize: "0.9rem" }}
                align="center"
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTeamMembers.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  align="center"
                  sx={{ py: 6, color: "text.secondary" }}
                >
                  {userSearchQuery ? (
                    <>
                      <SearchIcon
                        sx={{
                          fontSize: 64,
                          mb: 2,
                          opacity: 0.3,
                          color: "#1976d2",
                        }}
                      />
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        No users found
                      </Typography>
                      <Typography variant="body2">
                        Try adjusting your search terms or{" "}
                        <Button
                          onClick={clearUserSearch}
                          sx={{
                            textTransform: "none",
                            p: 0,
                            minWidth: "auto",
                          }}
                        >
                          clear search
                        </Button>
                      </Typography>
                    </>
                  ) : (
                    <>
                      <PeopleIcon
                        sx={{
                          fontSize: 64,
                          mb: 2,
                          opacity: 0.3,
                          color: "#1976d2",
                        }}
                      />
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        No users added yet
                      </Typography>
                      <Typography variant="body2">
                        Click "Add New User" to get started
                      </Typography>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              paginatedTeamMembers.map((member, index) => (
                <TableRow
                  key={member.lawfirm_user_id}
                  hover
                  sx={{
                    "&:hover": {
                      backgroundColor: "#f8f9fa",
                    },
                    borderBottom: "1px solid #e0e0e0",
                  }}
                >
                  <TableCell
                    sx={{ fontSize: "0.875rem", fontWeight: "500" }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      {member.is_admin && (
                        <AdminPanelSettingsIcon
                          color="primary"
                          fontSize="small"
                        />
                      )}
                      {member.lawfirm_user_name}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem" }}>
                    {member.firm_email ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.25,
                        }}
                      >
                        <Box component="span">
                          {member.firm_email}
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "0.75rem",
                              color: "primary.main",
                              fontWeight: 600,
                              ml: 0.5,
                            }}
                          >
                            (Firm)
                          </Typography>
                        </Box>
                        {member.email &&
                          member.email !== member.firm_email && (
                            <Tooltip
                              title="Primary email"
                              arrow
                              placement="top"
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  fontSize: "0.7rem",
                                  display: "block",
                                }}
                              >
                                {member.email}
                              </Typography>
                            </Tooltip>
                          )}
                      </Box>
                    ) : (
                      member.email || "N/A"
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem" }}>
                    {member.location || "N/A"}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.875rem" }}>
                    <Chip
                      label={member.Position || "N/A"}
                      size="small"
                      variant="outlined"
                      color="primary" // Changed to primary (blue) for consistency
                    />
                  </TableCell>
                  <TableCell align="center">
                    {member.is_admin ? (
                      <CheckCircleIcon color="success" fontSize="small" />
                    ) : (
                      <CancelIcon color="disabled" fontSize="small" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={member.is_active}
                      onChange={() =>
                        handleToggle(
                          teamMembersPage * teamMembersRowsPerPage + index,
                          "is_active",
                          setTeamMembers,
                          teamMembers
                        )
                      }
                      size="small"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={member.is_default}
                      onChange={() =>
                        handleToggle(
                          teamMembersPage * teamMembersRowsPerPage + index,
                          "is_default",
                          setTeamMembers,
                          teamMembers
                        )
                      }
                      size="small"
                      color="primary" // Changed to primary (blue) for consistency
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "center",
                      }}
                    >
                      {(role === "super_user" || role === "internal_user") && (
                        <Tooltip title="Re-assign Policies">
                          <IconButton
                            color="success"
                            size="small"
                            onClick={() => openReassignDialog(member)}
                            sx={{
                              "&:hover": {
                                backgroundColor: "success.main",
                                color: "white",
                              },
                            }}
                          >
                            <SwapHorizIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit User">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() =>
                            navigate(
                              `/editlawfirmuser/${member.lawfirm_user_id}/${member.location_id}`
                            )
                          }
                          sx={{
                            "&:hover": {
                              backgroundColor: "#1976d2",
                              color: "white",
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete User">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteUserClick(member)}
                          sx={{
                            "&:hover": {
                              backgroundColor: "error.main",
                              color: "white",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {filteredTeamMembers.length > 0 && (
        <TablePagination
          ActionsComponent={TablePaginationActions}
          component="div"
          count={filteredTeamMembers.length}
          page={teamMembersPage}
          onPageChange={handleTeamMembersPageChange}
          rowsPerPage={teamMembersRowsPerPage}
          onRowsPerPageChange={handleTeamMembersRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            backgroundColor: "#f8f9fa",
            borderTop: "1px solid #e0e0e0",
            "& .MuiTablePagination-toolbar": {
              color: "#1976d2",
            },
            "& .MuiTablePagination-selectIcon": {
              color: "#1976d2",
            },
          }}
        />
      )}
    </Box>
  );
}

export default LawFirmUsersTabPanel;
